import React, { useEffect, useReducer, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import { batch, connect } from 'react-redux';

import CommonSelectInputWrapper from '../../../../common/CommonSelectInputWrapper';
import GridColumnInputWrapper from '../../../../common/GridColumnInputWrapper';
import { AccordionWrapper, InputWrapper } from 'react-lib/dist/components-es';
import {
  LARGE_AMOUNT,
  MEDIUM_AMOUNT,
  RISK_TYPE_SUBSTANCE_AMOUNT_CODE,
  SMALL_AMOUNT,
} from '../../../../../constants';
import {
  isAdmin,
  isChemicalSafetyExpert,
  isEnvironmentalExpert,
} from '../../../../../behavior/init/selectors';

import { getQuantityType, getRiskOptions, setObjProp } from '../../../../../utils';
import { debounce } from 'lodash-es';
import { risksActions } from '../../../../../behavior/risks';
import i18n from 'i18next';
import useUser from '@behavior/init/getUserV2';

type Props = {
  envRiskCalculation: () => void;
  chemRiskCalculation: () => void;
  values: any;
  initialData: any;
  errors: any;
  setFieldValue: (arg1: string, arg2: string) => void;
  disabled: boolean;
};

const IChemicalContainer = ({
  envRiskCalculation,
  chemRiskCalculation,
  values,
  initialData,
  errors,
  setFieldValue,
  disabled,
}: Props) => {
  const [substanceAmountOptions, setSubstanceAmountOptions] = useState([]);
  const { user } = useUser();
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  useEffect(() => {
    initSubstanceAmountOptions();
  }, [i18n.language]);

  const initSubstanceAmountOptions = () => {
    setSubstanceAmountOptions(
      getRiskOptions(initialData.riskList, RISK_TYPE_SUBSTANCE_AMOUNT_CODE, values.substanceAmount)
    );
  };

  const debouncedOnQuantityChangeCallback = () => {
    updateSubstanceAmount();
    batch(() => {
      envRiskCalculation();
      chemRiskCalculation();
    });
  };

  const debouncedOnQuantityChange = debounce(debouncedOnQuantityChangeCallback, 700);

  const onQuantityChange = (callback: () => void) => {
    forceUpdate(); //this forces quantity value to be updated when the debounced callback will be triggered
    callback();
  };

  const updateSubstanceAmount = () => {
    let substanceAmountValue: number;
    let q = values.quantity != null && values.quantity != '' ? Number(values.quantity) : null;
    if (q != null) {
      if (q <= 1) substanceAmountValue = SMALL_AMOUNT;
      else if (q <= 1000) substanceAmountValue = MEDIUM_AMOUNT;
      else if (q > 1000) substanceAmountValue = LARGE_AMOUNT;
    }
    const substanceAmount = substanceAmountOptions.find(
      (p: any) => p.value == substanceAmountValue
    ) as any;
    values.substanceAmount.id = substanceAmount ? substanceAmount.value : '';
    values.substanceAmount.value = substanceAmount ? substanceAmount.text : '';
    forceUpdate();
  };

  const getChemicalContainerContent = () => {
    const hideSubstanceAmount = !(
      isChemicalSafetyExpert(user) ||
      isEnvironmentalExpert(user) ||
      isAdmin(user)
    );

    if (!values.substanceAmount) values.substanceAmount = {};
    let quantityType = getQuantityType(values.chemicalProduct);
    return (
      <>
        <Grid stackable doubling columns={4} verticalAlign="bottom">
          <GridColumnInputWrapper
            name="quantity"
            label="inventory.details.page.chemical.container.quantity.input"
            errors={errors}
            setFieldValue={setFieldValue}
            disabled={disabled}
            render={(props: any) => (
              <InputWrapper
                {...props}
                inputLabel={quantityType ? { basic: true, content: quantityType } : null}
                inputLabelPosition={quantityType ? 'right' : null}
                onChange={() => onQuantityChange(debouncedOnQuantityChange)}
                type="number"
                value={
                  values.quantity !== null && values.quantity !== undefined
                    ? values.quantity.toString()
                    : ''
                }
                min={0}
              />
            )}
          />
          <GridColumnInputWrapper
            name="substanceAmount"
            label="inventory.details.page.chemical.inf.container.substance.amount.input"
            disabled={true}
            fieldClassName={hideSubstanceAmount ? 'hiddenField' : ''}
            errors={errors}
            setFieldValue={setFieldValue}
            render={(props: any) => (
              <CommonSelectInputWrapper
                value={values.substanceAmount.id || ''}
                options={substanceAmountOptions}
                onChange={chemRiskCalculation}
                {...props}
              />
            )}
          />
        </Grid>
      </>
    );
  };

  return (
    <AccordionWrapper
      closable={false}
      accordionTitle="inventory.details.page.chemical.container.title"
      render={getChemicalContainerContent}
      className=""
    />
  );
};

const mapStateToProps = (state: any) => ({
  stateChange: state.chemicalProducts.stateChange,
});

const mapDispatchToProps = (dispatch: any) => ({
  envRiskCalculation: () => dispatch(risksActions.envRiskCalculation()),
  chemRiskCalculation: () => dispatch(risksActions.chemRiskCalculation()),
});

export default connect(mapStateToProps, mapDispatchToProps)(IChemicalContainer);
